import { useEffect, useState, useRef } from 'react'
import InlineRenameInput from '@/components/InlineRenameInput'
import { useAppStore } from '@/store'
import { useTranslation } from '@/i18n'
import { ConnectorRecord } from '@/types/connector'
import { getConnectorStatus } from '@/lib/getConnectorStatus'
import { ConnectorStatus, ConnectorStatusType } from '@/types/connector'

interface ConnectorCardProps {
  id: string | undefined
}

interface ConnectorStatusProps {
  status: ConnectorStatusType | undefined
}

const ConnectorStatusHeader = ({ status }: ConnectorStatusProps) => (
  <span
    className={`w-full inline py-1 px-3 text-center text-gray-600 dark:text-gray-300 capitalize ${
      status === ConnectorStatus.ONLINE ? 'bg-green-100 dark:bg-green-900/30' : 'bg-gray-200 dark:bg-dark-border'
    }`}
  >
    {status}
  </span>
)

const ConnectorCard = ({ id }: ConnectorCardProps) => {
  const { t } = useTranslation()
  const { fetchConnectorById } = useAppStore()
  const updateConnector = useAppStore((s) => s.updateConnector)
  const [currentConnector, setConnector] = useState<
    ConnectorRecord | undefined
  >(undefined)
  const [connectorStatus, setConnectorStatus] = useState<
    ConnectorStatusType | undefined
  >(undefined)

  // Inline rename state (mirrors the document card's rename affordance).
  const [isRenaming, setIsRenaming] = useState(false)
  const [editValue, setEditValue] = useState('')
  const renameInputRef = useRef<HTMLInputElement>(null)
  const isSavingRef = useRef(false)
  // Pause the 10s poll from overwriting the card (and the optimistic name) while
  // the user is mid-rename.
  const isRenamingRef = useRef(false)
  isRenamingRef.current = isRenaming
  // Also pause it while the rename PUT is in flight: saveRename calls
  // setIsRenaming(false) up front, so isRenamingRef clears before the async
  // persist resolves. Without this guard a poll landing mid-PUT would refetch the
  // pre-rename name and snap the optimistic update back.
  const isPendingUpdateRef = useRef(false)

  // Use ref to hold the fetch function to prevent interval recreation when store function changes
  const fetchConnectorByIdRef = useRef(fetchConnectorById)
  fetchConnectorByIdRef.current = fetchConnectorById

  useEffect(() => {
    if (!id) return

    const fetchData = async () => {
      if (isRenamingRef.current || isPendingUpdateRef.current) return
      const foundConnector = await fetchConnectorByIdRef.current(id)
      if (foundConnector && !isRenamingRef.current && !isPendingUpdateRef.current) {
        setConnector(foundConnector)
        setConnectorStatus(getConnectorStatus(foundConnector))
      }
    }

    fetchData()
    const interval = setInterval(fetchData, 10000)
    return () => clearInterval(interval)
  }, [id]) // Only re-run when id changes, not when fetchConnectorById changes

  const startRenaming = () => {
    if (!currentConnector) return
    isSavingRef.current = false
    setEditValue(currentConnector.name)
    setIsRenaming(true)
    setTimeout(() => {
      renameInputRef.current?.focus()
      renameInputRef.current?.select()
    }, 0)
  }

  const saveRename = () => {
    if (isSavingRef.current) return
    isSavingRef.current = true
    const trimmed = editValue.trim()
    setIsRenaming(false)
    if (id && currentConnector && trimmed && trimmed !== currentConnector.name) {
      // Optimistic local update; updateConnector persists + the resumed poll
      // reconciles (reverting if the backend update fails). Guard the poll until
      // the PUT settles so it can't refetch the pre-rename name mid-flight.
      setConnector({ ...currentConnector, name: trimmed })
      isPendingUpdateRef.current = true
      Promise.resolve(updateConnector(id, { name: trimmed })).finally(() => {
        isPendingUpdateRef.current = false
      })
    }
  }

  const cancelRename = () => {
    isSavingRef.current = true // prevent onBlur from re-triggering saveRename
    setIsRenaming(false)
  }

  return currentConnector ? (
    <div className="w-full h-full flex flex-col items-center bg-white dark:bg-dark-card shadow-[0_2px_12px_rgba(15,23,42,0.08)] rounded-xl border border-[#e8eaee] dark:border-dark-border cursor-pointer overflow-hidden">
      <ConnectorStatusHeader status={connectorStatus} />
      <div className="w-full h-full flex gap-4 items-center justify-between py-0 px-4">
        <div className="flex gap-3 items-center min-w-0">
          {isRenaming ? (
            // Same typography as the display <h2>, so the card header keeps
            // its exact footprint while renaming.
            <InlineRenameInput
              ref={renameInputRef}
              value={editValue}
              onChange={(e) => setEditValue(e.target.value)}
              onKeyDown={(e) => {
                e.stopPropagation()
                if (e.key === 'Enter') {
                  e.preventDefault()
                  saveRename()
                } else if (e.key === 'Escape') {
                  e.preventDefault()
                  cancelRename()
                }
              }}
              onBlur={saveRename}
              onPointerDown={(e) => e.stopPropagation()}
              onClick={(e) => e.stopPropagation()}
              maxLength={255}
              className="font-semibold text-[18px] truncate"
            />
          ) : (
            <h2
              className="font-semibold text-[18px] truncate cursor-pointer hover:text-blue-600 transition-colors"
              title={t('board:actions.click_to_rename', { defaultValue: 'Click to rename' })}
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation()
                startRenaming()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  e.stopPropagation()
                  startRenaming()
                }
              }}
              onPointerDown={(e) => e.stopPropagation()}
            >
              {currentConnector.name}
            </h2>
          )}
        </div>
      </div>
    </div>
  ) : (
    <div className="w-full h-full flex flex-col items-center bg-white dark:bg-dark-card shadow-[0_2px_12px_rgba(15,23,42,0.08)] rounded-xl border border-[#e8eaee] dark:border-dark-border cursor-pointer overflow-hidden">
      {t('board:connector_card.fetch_error', { defaultValue: 'Error fetching connector data' })}
    </div>
  )
}

export default ConnectorCard
